import { useContext, useEffect, useMemo, useState, type FC } from "react";

import AppContext from "../../context/AppContext";
import { AppStrings } from "../../utils/constants/AppStrings";
import { SIDER_TABS } from "../../utils/Constants";
import { useStaffRegulatorsList } from "../../queries/staff-regulators/useStaffRegulatorsList";
import type { StaffRegulator } from "../../queries/staff-regulators/types";
import { filterStaffRegulators } from "./staffRegulatorsData";
import { staffRegulatorsErrorMessage } from "./staffRegulatorsErrorMessage";
import StaffRegulatorsList from "./StaffRegulatorsList/StaffRegulatorsList";
import { usePageTitle } from "../CommonComponents/Layout/PageTitleContext";

import styles from "./StaffRegulators.module.css";

type AppContextValue = {
  setActiveSidebarTab: (
    tabName: string,
    dropdownOption?: string | false,
  ) => void;
};

const StaffRegulators: FC = () => {
  usePageTitle(AppStrings.COMPLIANCE_REGULATORS);

  const { setActiveSidebarTab } = useContext(AppContext) as AppContextValue;
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StaffRegulator["status"] | undefined>(
    undefined,
  );
  const listQuery = useStaffRegulatorsList();

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS?.COMPLIANCE, "COMPLIANCE_REGULATORS");
  }, [setActiveSidebarTab]);

  const rows = useMemo(
    () => filterStaffRegulators(listQuery.data ?? [], search, status),
    [listQuery.data, search, status],
  );

  const loadError =
    listQuery.isError && !listQuery.data
      ? staffRegulatorsErrorMessage(
          listQuery.error,
          AppStrings.COMPLIANCE_REGULATORS_LOAD_ERROR,
        )
      : null;

  return (
    <>
      <div className={styles.container} data-testid="staff-regulators">
        {loadError ? <p role="alert">{loadError}</p> : null}
        {!loadError &&
        !listQuery.isLoading &&
        (listQuery.data ?? []).length === 0 ? (
          <p>{AppStrings.COMPLIANCE_REGULATORS_EMPTY}</p>
        ) : null}
        <StaffRegulatorsList
          rows={rows}
          loading={listQuery.isLoading}
          filters={{
            search,
            onSearchChange: setSearch,
            status,
            onStatusChange: setStatus,
          }}
        />
      </div>
    </>
  );
};

export default StaffRegulators;
